import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import { localStorageAvailable } from 'src/utils/storage-available';
import { ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import Avatar from '@mui/material/Avatar';
// routes
import { useRouter, useSearchParams } from 'src/routes/hook';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import { useDispatch, useSelector } from '../../../redux/store';
import { useLocales } from '../../../locales';
import { getChain, getChains } from '../../../redux/slices/chain';
import { ChainDto } from '../../../api';
import { KEYS } from '../../../utils/constants';

// ----------------------------------------------------------------------

export default function JwtLoginView() {
  const { login } = useAuthContext();

  const { t } = useLocales();

  const router = useRouter();

  const [errorMsg, setErrorMsg] = useState('');

  const searchParams = useSearchParams();

  const returnTo = searchParams.get('returnTo');

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required(`${t('Email is required')}`)
      .email(`${t('Email must be a valid email address')}`),
    password: Yup.string().required(`${t('Password is required')}`),
    chain: Yup.object().required(`${t('Chain is required')}`),
  });

  const [inputValue, setInputValue] = useState('');

  const chains = useSelector((state) => state.chain.chains);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getChains(inputValue));
  }, [dispatch, inputValue]);

  const defaultValues = {
    email: '',
    password: '',
    chain: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const storageAvailable = localStorageAvailable();

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const chain = data.chain as ChainDto;

      await login?.(data.email, data.password, chain.id!);

      // Fetch now otherwise it isn't ready for the dashboard
      await dispatch(getChain());

      if (storageAvailable) {
        localStorage.removeItem(KEYS.selectedGymIdKey);
      }

      router.push(returnTo || PATH_AFTER_LOGIN);
    } catch (error) {
      reset();
      setErrorMsg(typeof error === 'string' ? error : error.response?.data?.detail);
    }
  });

  const renderHead = (
    <Stack spacing={2} sx={{ mb: 5, position: 'relative' }}>
      <Typography variant="h4">{`${t('Hi, Welcome back')}`}</Typography>
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      <RHFTextField name="email" label={`${t('Email address')}`} />

      <RHFTextField
        name="password"
        label={`${t('Password')}`}
        type={showPassword ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <RHFAutocomplete
        name="chain"
        options={chains}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name ?? '')}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        label={`${t('Chain')}`}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderOption={(props, option) => (
          <ListItem {...props} key={option.id}>
            <ListItemAvatar>
              <Avatar
                src={option?.logoUrl}
                alt={option?.name}
                sx={{
                  width: 36,
                  height: 36,
                  border: (theme) => `solid 2px ${theme.palette.background.default}`,
                }}
              />
            </ListItemAvatar>
            <ListItemText primary={option.name} />
          </ListItem>
        )}
      />

      <Button
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        {`${t('Login')}`}
      </Button>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}
      {renderForm}
    </FormProvider>
  );
}
